import React, { lazy } from 'react'
import ErrorBoundary from '../../components/ErrorBoundary'

// Loading component for Suspense fallbacks
export const LoadingSpinner = ({ message = 'Loading...' }) => (
  <div className="loading-spinner">
    <div className="spinner">
      <div className="bounce1"></div>
      <div className="bounce2"></div>
      <div className="bounce3"></div>
    </div>
    <p>{message}</p>
  </div>
)

// Enhanced lazy loading with error boundary
export const createLazyComponent = (importFn, fallbackMessage) => {
  const LazyComponent = lazy(importFn)

  return (props) => (
    <ErrorBoundary
      fallback={(error, retry) => (
        <div className="lazy-load-error">
          <p>Failed to load component. Please try again.</p>
          <button onClick={retry}>Retry</button>
        </div>
      )}
    >
      <LazyComponent {...props} />
    </ErrorBoundary>
  )
}

// Lazy load main pages
export const LazyHomePage = createLazyComponent(
  () => import('../../pages/site/Home'),
  'Loading home page...'
)

export const LazyAboutUs = createLazyComponent(
  () => import('../../pages/site/AboutUs'),
  'Loading about page...'
)

export const LazyServices = createLazyComponent(
  () => import('../../pages/site/Services'),
  'Loading services...'
)

export const LazyTeam = createLazyComponent(
  () => import('../../pages/site/Team'),
  'Loading team page...'
)

export const LazyLibrary = createLazyComponent(
  () => import('../../pages/site/Library'),
  'Loading library...'
)

export const LazyGalleryPage = createLazyComponent(
  () => import('../../pages/site/Gallery'),
  'Loading gallery...'
)

// Lazy load authentication pages
export const LazyLoginPage = createLazyComponent(
  () => import('../../pages/site/Login'),
  'Loading login page...'
)

export const LazySignUpPage = createLazyComponent(
  () => import('../../pages/site/SignUp'),
  'Loading signup page...'
)

// Lazy load AI and chat components
export const LazyKAI = createLazyComponent(
  () => import('../../pages/site/KAI'),
  'Loading AI assistant...'
)

export const LazyChatPage = createLazyComponent(
  () => import('../../pages/site/Chat'),
  'Loading chat...'
)

export const LazyAIChat = createLazyComponent(
  () => import('../../components/AIChat'),
  'Loading AI chat...'
)

// Lazy load dashboard components
export const LazyStudentDashboard = createLazyComponent(
  () => import('../../components/StudentDashboard'),
  'Loading student dashboard...'
)

export const LazyStudentAmbassadorDashboard = createLazyComponent(
  () => import('../../components/StudentAmbassadorDashboard'),
  'Loading ambassador dashboard...'
)

export const LazyAdminDashboard = createLazyComponent(
  () => import('../../components/AdminDashboard'),
  'Loading admin dashboard...'
)

export const LazyUnifiedDashboard = createLazyComponent(
  () => import('../../components/UnifiedDashboard'),
  'Loading dashboard...'
)

// Lazy load specific dashboard sections
export const LazyStudentDashboardHome = createLazyComponent(
  () => import('../../components/StudentDashboard/Home'),
  'Loading dashboard home...'
)

export const LazyStudentDashboardCourses = createLazyComponent(
  () => import('../../components/StudentDashboard/Courses'),
  'Loading courses...'
)

export const LazyStudentDashboardResources = createLazyComponent(
  () => import('../../components/StudentDashboard/Resources'),
  'Loading resources...'
)

export const LazyStudentDashboardChats = createLazyComponent(
  () => import('../../components/StudentDashboard/Chats'),
  'Loading chats...'
)

export const LazyStudentDashboardProfile = createLazyComponent(
  () => import('../../components/StudentDashboard/Profile'),
  'Loading profile...'
)

// Lazy load admin components
export const LazyAdminDashboardHome = createLazyComponent(
  () => import('../../components/AdminDashboard/Home'),
  'Loading admin home...'
)

export const LazyAdminDashboardUsers = createLazyComponent(
  () => import('../../components/AdminDashboard/System'),
  'Loading user management...'
)

export const LazyAdminDashboardResources = createLazyComponent(
  () => import('../../components/AdminDashboard/Resources'),
  'Loading resource management...'
)

export const LazyAdminDashboardCourses = createLazyComponent(
  () => import('../../components/AdminDashboard/Courses'),
  'Loading course management...'
)

export const LazyAdminDashboardApprovals = createLazyComponent(
  () => import('../../components/AdminDashboard/Approvals'),
  'Loading approvals...'
)

// Lazy load ambassador components
export const LazyAmbassadorDashboardHome = createLazyComponent(
  () => import('../../components/StudentAmbassadorDashboard/Home'),
  'Loading ambassador home...'
)

export const LazyAmbassadorDashboardEvents = createLazyComponent(
  () => import('../../components/StudentAmbassadorDashboard/Events'),
  'Loading events...'
)

export const LazyAmbassadorDashboardMentoring = createLazyComponent(
  () => import('../../components/StudentAmbassadorDashboard/Mentoring'),
  'Loading mentoring...'
)

// Lazy load utility pages
export const LazyDonations = createLazyComponent(
  () => import('../../pages/site/Donations'),
  'Loading donations...'
)

export const LazyResourceDetail = createLazyComponent(
  () => import('../../pages/site/ResourceDetail'),
  'Loading resource details...'
)

export const LazyNotFound404Page = createLazyComponent(
  () => import('../../pages/site/NotFound404'),
  'Loading error page...'
)

// Lazy load testing and development pages
export const LazyRbacTest = createLazyComponent(
  () => import('../../pages/site/RbacTest'),
  'Loading RBAC test...'
)

export const LazyFeatureFlagTest = createLazyComponent(
  () => import('../../pages/site/FeatureFlagTest'),
  'Loading feature flags...'
)

export const LazyDevTools = createLazyComponent(
  () => import('../../pages/site/DevTools'),
  'Loading dev tools...'
)

export const LazyPaymentTest = createLazyComponent(
  () => import('../../pages/site/PaymentTest'),
  'Loading payment test...'
)

export const LazyCompetitionTest = createLazyComponent(
  () => import('../../pages/site/CompetitionTest'),
  'Loading competition test...'
)

// Preloading utilities
export const preloadComponent = (importFn) => {
  const componentImport = importFn()
  // Don't wait for it, just start the import
  return componentImport
}

// Preload critical components
export const preloadCriticalComponents = () => {
  // Preload components likely to be needed soon
  preloadComponent(() => import('../../pages/site/Home'))
  preloadComponent(() => import('../../pages/site/Login'))
  preloadComponent(() => import('../../components/UnifiedDashboard'))
}

// Component with intersection observer for lazy loading
export const LazyComponentWithIntersection = ({
  importFn,
  fallback,
  rootMargin = '50px',
}) => {
  const [shouldLoad, setShouldLoad] = React.useState(false)
  const ref = React.useRef()

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [rootMargin])

  const LazyComponent = React.useMemo(() => {
    return shouldLoad ? lazy(importFn) : null
  }, [shouldLoad, importFn])

  return (
    <div ref={ref}>
      {LazyComponent ? (
        <React.Suspense fallback={fallback}>
          <LazyComponent />
        </React.Suspense>
      ) : (
        fallback
      )}
    </div>
  )
}
